/**
 * ============================================================================
 * VEDANG JYOTISHAM V5.16 — MASTER TYPE DEFINITIONS (AS PER BRD / MASTER SPEC)
 * ============================================================================
 * Zero 'Learner' residue. Four presentation tiers, orthogonal operational RBAC,
 * complete astronomical core, divisional charts D1-D60, Shadbala, Bhavabala,
 * Ashtakavarga, Vimshottari 5-tier Dasha, verified Jaimini scope, prediction
 * window contracts, V5.16 additive modules (Muhurta, Transits, CRM Invoicing,
 * Prashna, Varshaphala), and Deterministic Evidence Pack for AI Gateway V2.
 */

// 1. PRESENTATION TIERS & RBAC (ZERO LEARNER RESIDUE)
export const APP_VERSION = 'V6.5';

export type PresentationTier = 'GUEST' | 'BASIC' | 'PRO_ASTRO' | 'ACHARYA';

export interface TierEntitlements {
  // Computational Gates
  maxVargaLevel: number;
  maxDashaDepth: number; // 3 for Basic so engine can compute down to Pratyantar
  allowAshtakavargaMatrix: boolean;
  allowKakshyaTransits: boolean;
  allowCharaDasha: boolean;
  allowVarshaphala: boolean;
  allowPrashna: boolean;
  aiConsultationEnabled: boolean;
  dossierExportType: 'WATERMARKED_PREVIEW' | 'PDF_SUMMARY' | 'FULL_TECHNICAL' | 'WHITE_LABEL';

  // Presentation & UI Privacy Gates
  presentationMode: 'HUMAN_SIMPLE' | 'TECHNICAL_INTERACTIVE';
  showTechnicalBadges: boolean;       // false for Basic, true for Pro/Acharya
  showClassicalCitations: boolean;     // false for Basic, true for Pro/Acharya
  activeTimingView: 'MIND_AND_FOCUS' | 'FULL_DASHA_TREE'; // Basic gets plain-English focus
}

export const TIER_ENTITLEMENT_MATRIX: Record<PresentationTier, TierEntitlements> = {
  GUEST: {
    maxVargaLevel: 9,
    maxDashaDepth: 1,
    allowAshtakavargaMatrix: false,
    allowKakshyaTransits: false,
    allowCharaDasha: false,
    allowVarshaphala: false,
    allowPrashna: false,
    aiConsultationEnabled: false,
    dossierExportType: 'WATERMARKED_PREVIEW',
    presentationMode: 'HUMAN_SIMPLE',
    showTechnicalBadges: false,
    showClassicalCitations: false,
    activeTimingView: 'MIND_AND_FOCUS'
  },
  BASIC: {
    maxVargaLevel: 10,
    maxDashaDepth: 3,
    allowAshtakavargaMatrix: false,
    allowKakshyaTransits: false,
    allowCharaDasha: false,
    allowVarshaphala: false,
    allowPrashna: false,
    aiConsultationEnabled: true,
    dossierExportType: 'PDF_SUMMARY',
    presentationMode: 'HUMAN_SIMPLE',
    showTechnicalBadges: false,
    showClassicalCitations: false,
    activeTimingView: 'MIND_AND_FOCUS'
  },
  PRO_ASTRO: {
    maxVargaLevel: 60,
    maxDashaDepth: 5,
    allowAshtakavargaMatrix: true,
    allowKakshyaTransits: true,
    allowCharaDasha: true,
    allowVarshaphala: true,
    allowPrashna: true,
    aiConsultationEnabled: true,
    dossierExportType: 'FULL_TECHNICAL',
    presentationMode: 'TECHNICAL_INTERACTIVE',
    showTechnicalBadges: true,
    showClassicalCitations: true,
    activeTimingView: 'FULL_DASHA_TREE'
  },
  ACHARYA: {
    maxVargaLevel: 60,
    maxDashaDepth: 5,
    allowAshtakavargaMatrix: true,
    allowKakshyaTransits: true,
    allowCharaDasha: true,
    allowVarshaphala: true,
    allowPrashna: true,
    aiConsultationEnabled: true,
    dossierExportType: 'WHITE_LABEL',
    presentationMode: 'TECHNICAL_INTERACTIVE',
    showTechnicalBadges: true,
    showClassicalCitations: true,
    activeTimingView: 'FULL_DASHA_TREE'
  }
};

export type OperationalRole = 
  | 'MANAGEMENT' 
  | 'CONSULTING_ASTROLOGER' 
  | 'TESTER' 
  | 'SUPER_ADMIN';

export type ProfileType = 
  | 'SELF' 
  | 'FAMILY' 
  | 'SPOUSE_PARTNER' 
  | 'CHILD' 
  | 'CLIENT' 
  | 'SAVED_KUNDALI';

export type SupportedLocale = 
  | 'en' | 'hi' | 'pa' | 'mr' | 'gu' 
  | 'bn' | 'ta' | 'te' | 'kn' | 'ml' 
  | 'hinglish';

// 2. ASTRONOMICAL & ASTROLOGICAL CORE TYPES
export type GrahaKey = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

export interface GeoLocation {
  latitude: number;
  longitude: number;
  altitudeMeters?: number;
  timezoneOffsetHours: number;
}

export interface BirthInput {
  isoDateTime: string; // ISO 8601 string
  location: GeoLocation;
  name?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
}

export interface PlanetaryPosition {
  graha: GrahaKey;
  longitude: number; // 0 to 359.9999
  speed: number;
  declination: number;
  rashi: number; // 1 to 12
  rashiDegree: number; // 0 to 29.9999
  nakshatra: number; // 1 to 27
  pada: number; // 1 to 4
  house: number; // 1 to 12
  isRetrograde: boolean;
  isCombust: boolean;
}

export interface HouseCusp {
  houseNumber: number; // 1 to 12
  startLongitude: number;
  middleLongitude: number;
  endLongitude: number;
  rashi: number;
}

export type VargaKey = 
  | 'D1' | 'D2' | 'D3' | 'D4' | 'D7' | 'D9' | 'D10' 
  | 'D12' | 'D16' | 'D20' | 'D24' | 'D27' | 'D30' 
  | 'D40' | 'D45' | 'D60';

export interface VargaChartData {
  varga: VargaKey;
  ascendantRashi: number;
  planetarySigns: Record<GrahaKey, number>;
}

// 3. STRENGTHS, BHAVABALA & ASHTAKAVARGA
export interface SthanaBalaBreakdown {
  uchhaBala: number;
  saptavargiyaBala: number;
  ojayugmaBala: number;
  kendraBala: number;
  drekanaBala: number;
  total: number;
}

export interface ShadbalaResult {
  graha: GrahaKey;
  sthanaBala: SthanaBalaBreakdown;
  digBala: number;
  kalaBala: number;
  cheshtaBala: number;
  drikBala: number;
  ayanaBala: number;
  totalShadbala: number;
  shadbalaRatio: number; // Actual / Required
}

export interface BhavabalaResult {
  houseNumber: number;
  bhavaAdhipatiBala: number;
  bhavaDigBala: number;
  bhavaDrishtiBala: number;
  totalBhavabala: number;
}

export interface AshtakavargaMatrix {
  bav: Record<GrahaKey, number[]>; // 12 houses bindus per planet
  sav: number[]; // 12 houses bindus total (337 max)
  shodhyaPinda: Record<GrahaKey, number>;
}

// 4. PREDICTION & TEMPORAL ENGINE (5-TIER DASHA & WINDOW CONTRACT)
export interface PlainLanguageInterpretation {
  /**
   * Plain-language explanation of everyday life impact.
   * STRICT STRUCTURAL INVARIANT: Must be written at an 8th-grade reading level.
   * Must contain ZERO astrological jargon, Sanskrit terms, house numbers, or planetary names.
   */
  lifeImpactSummary: string;

  /** Practical, actionable advice (1-2 clear steps to take or avoid). */
  actionableGuidance: string | string[];

  /** Astrological / technical factor rendered purely as a small metadata tag/badge. */
  technicalFactor: string;

  /** Optional classical citation rendered only in scholarly footers or tooltips. */
  classicalSource?: string;
}

export interface PredictionWindowV62 {
  startDate: string;        // YYYY-MM-DD
  endDate: string;          // YYYY-MM-DD
  peakDate: string;         // YYYY-MM-DD
  confidenceScore: number;  // 0.0 to 1.0
  convergenceLevel: number; // 1 to 5
  interpretation: PlainLanguageInterpretation;
}

export interface PredictionWindow {
  startDate: string;        // YYYY-MM-DD
  endDate: string;          // YYYY-MM-DD
  peakDate: string;         // YYYY-MM-DD
  confidenceScore: number;  // 0.0 to 1.0
  convergenceLevel: number; // 1 to 5 triangulation strength
  humanMeaning: string;     // Universal Human-First rule 1
  technicalTerm: string;    // Universal Human-First rule 2
  classicalDetail: string;  // Universal Human-First rule 3
}

export interface DashaNode {
  graha: GrahaKey;
  level: 'MAHA' | 'ANTAR' | 'PRATYANTAR' | 'SOOKSHMA' | 'PRANA';
  startDate: string;
  endDate: string;
  subDashas?: DashaNode[];
}

// 5. JAIMINI & SYNASTRY SCOPE
export interface JaiminiScope {
  charaKarakas: Record<string, GrahaKey>; // Atmakaraka through Darakaraka
  arudhaLagna: number; // 1 to 12
  upapadaLagna: number; // 1 to 12
  charaDashaCurrent: { sign: number; startDate: string; endDate: string };
  rashiDrishti: Record<number, number[]>;
}

export interface GunaMilanScore {
  varna: { points: number; max: 1 };
  vashya: { points: number; max: 2 };
  tara: { points: number; max: 3 };
  yoni: { points: number; max: 4 };
  grahaMaitri: { points: number; max: 5 };
  gana: { points: number; max: 6 };
  bhakoot: { points: number; max: 7; doshaMitigated: boolean };
  nadi: { points: number; max: 8; doshaMitigated: boolean };
  totalScore: number; // out of 36
  archetype: 'MARRIAGE' | 'BUSINESS' | 'PARENT_CHILD';
}

// 6. V6.1 ADDITIVE MODULE TYPES (PHASES 1, 2, 3)
export interface MuhurtaCandidate {
  startDateTime: string;
  endDateTime: string;
  panchangaScore: number; // 0 to 100
  tithi: string;
  vara: string;
  nakshatra: string;
  yoga: string;
  karana: string;
  lagnaRashi: number;
  doshasAvoided: string[];
  doshasPresent: string[];
  isAuspicious: boolean;
  humanSummary: string;
}

export interface KakshyaTransitScore {
  graha: GrahaKey;
  transitingSign: number;
  kakshyaLord: GrahaKey | 'Lagna';
  hasBindu: boolean;
  transitStrengthFactor: number;
}

export interface PracticeInvoice {
  invoiceId: string;
  clientId: string;
  consultantId: string;
  amount: number;
  currency: 'INR' | 'USD';
  status: 'DRAFT' | 'ISSUED' | 'PAID';
  issuedAt: string;
  items: { description: string; unitPrice: number; quantity: number }[];
}

export interface PracticeClient {
  clientId: string;
  name: string;
  email: string;
  phone: string;
  birthDetails: BirthInput;
  assignedConsultantId?: string;
  notes: string;
  createdAt: string;
}

export interface PrashnaChart {
  queryIsoDateTime: string;
  questionCategory: 'CAREER' | 'RELATIONSHIP' | 'HEALTH' | 'TRAVEL' | 'FINANCE';
  prashnaLagna: number;
  daivajnaPlanetaryHour: GrahaKey;
  lagnesh: GrahaKey;
  karyesh: GrahaKey;
  tajikaYoga: 'ITHASHALA' | 'ISHRAFA' | 'NAKTA' | 'YAMAYA' | 'KAMBOOLA' | 'NONE';
  outcomeVerdict: 'FAVORABLE' | 'UNFAVORABLE' | 'OBSTACLES_THEN_SUCCESS';
  humanMeaning: string;
  technicalTerm: string;
  classicalDetail: string;
}

export interface VarshaphalaData {
  birthYear: number;
  targetYear: number;
  exactSolarReturnIso: string;
  varshaLagna: number;
  munthaRashi: number;
  munthaLord: GrahaKey;
  varsheshwaraCandidates: { graha: GrahaKey; score: number }[];
  varsheshwaraLord: GrahaKey;
  sahams: {
    punyaSaham: number;
    vidyaSaham: number;
    yashasSaham: number;
    karmaSaham: number;
    rogSaham: number;
    vivahaSaham: number;
  };
}

// 7. DETERMINISTIC EVIDENCE PACK FOR AI GATEWAY V2 & CIRCUIT BREAKER
export interface DeterministicEvidencePack {
  profileId: string;
  ascendantDegree: number;
  planetaryPositions: Record<GrahaKey, PlanetaryPosition>;
  vargas: Partial<Record<VargaKey, VargaChartData>>;
  shadbala: Record<GrahaKey, ShadbalaResult>;
  bhavabala: Record<number, BhavabalaResult>;
  ashtakavarga: AshtakavargaMatrix;
  currentDasha: { maha: GrahaKey; antar: GrahaKey; pratyantar: GrahaKey };
  jaimini: JaiminiScope;
  predictionWindows?: PredictionWindow[];
  prashna?: PrashnaChart;
  varshaphala?: VarshaphalaData;
  muhurta?: MuhurtaCandidate[];
  yogas?: { name: string; category: string; fruition: string; description: string }[];
  manglik?: any;
  kaalSarp?: any;
  acharyaPauseTriggered?: boolean;
  acharyaPauseReason?: string;
  contradictionFlags?: { houseNumber: number; issueEn: string }[];
  btrStatus?: {
    recordedTime: string;
    workingTime: string;
    candidateInterval?: string;
    confirmedTime?: string | null;
    confirmationStatus: 'UNCONFIRMED' | 'CONFIRMED';
    justifiedPrecision?: string;
    confidence: string;
    isRectified: boolean;
  };
}

export interface CircuitBreakerState {
  isPaused: boolean;
  violationCount: number;
  lastTriggerReason?: string;
  resetTimestamp?: string;
}

// 8. BTR V6.5 CONTRACTS & TWO-AXIS TYPES
export * from './btr';
